import { useCallback, useEffect, useState } from 'react';
import { deleteVersion, listVersions, thumbUrl, updateVersion, type VersionMeta } from '../data/versions.ts';
import { useEditor } from './store.ts';
import { openVersionAsCopy, restoreVersion, saveVersion } from './autosave.ts';
import { Icon } from './widgets.tsx';

function ago(iso: string): string {
  const s = (Date.now() - Date.parse(iso)) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
}

export function HistoryDialog() {
  const s = useEditor();
  const doc = s.doc;
  const [list, setList] = useState<VersionMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => s.set({ modal: null });

  const reload = useCallback(async () => {
    if (!doc) return;
    try {
      setList(await listVersions(doc.id));
      setError(null);
    } catch {
      setError('Version history needs the server, and it cannot be reached right now. Your edits are still saved on this device.');
    }
  }, [doc?.id]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) s.notify(done);
    } catch (e) {
      s.notify((e as Error).message);
    } finally {
      setBusy(false);
      void reload();
    }
  };

  if (!doc) return null;
  const pinned = list?.filter((v) => v.pinned) ?? [];
  const auto = list?.filter((v) => !v.pinned) ?? [];
  const totalKb = Math.round((list ?? []).reduce((a, v) => a + v.size, 0) / 1024);

  const card = (v: VersionMeta) => (
    <div key={v.id} className={`version-card ${v.pinned ? 'pinned' : ''}`}>
      <div className="version-thumb">{v.hasThumb ? <img src={thumbUrl(v.levelId, v.id)} alt="" loading="lazy" /> : 'no preview'}</div>
      <div className="version-info">
        <b title={v.label ?? undefined}>{v.label ?? (v.pinned ? 'Kept version' : 'Autosave')}</b>
        <span className="muted small" title={new Date(v.createdAt).toLocaleString()}>
          {ago(v.createdAt)} · {Math.max(1, Math.round(v.size / 1024))} KB{v.name !== doc.name ? ` · “${v.name}”` : ''}
        </span>
      </div>
      <div className="version-actions">
        <button
          className="btn"
          disabled={busy}
          onClick={() => {
            if (!confirm('Replace the level with this version? The current state is kept in the history, and Undo also works.')) return;
            void run(async () => {
              await restoreVersion(v);
              close();
            }, 'Version restored');
          }}
        >
          <Icon name="undo" size={14} /> Restore
        </button>
        <button className="icon-btn" title="Open as a new level" disabled={busy} onClick={() => void run(() => openVersionAsCopy(v), 'Opened as a new level')}>
          <Icon name="copy" size={16} />
        </button>
        <button
          className={`icon-btn ${v.pinned ? 'on' : ''}`}
          title={v.pinned ? 'Rename or stop keeping (it may then be thinned out)' : 'Keep this version (name it)'}
          disabled={busy}
          onClick={() => {
            if (v.pinned) {
              const n = prompt('Name (clear it to stop keeping this version)', v.label ?? '');
              if (n === null) return;
              void run(() => updateVersion(v.levelId, v.id, n.trim() ? { label: n } : { pinned: false, label: null }));
            } else {
              const n = prompt('Name this version', v.label ?? '');
              if (n === null) return;
              void run(() => updateVersion(v.levelId, v.id, { pinned: true, label: n }), 'Version kept');
            }
          }}
        >
          <Icon name="star" size={16} />
        </button>
        <button
          className="icon-btn"
          title="Delete version"
          disabled={busy}
          onClick={() => {
            if (confirm('Delete this version?')) void run(() => deleteVersion(v.levelId, v.id));
          }}
        >
          <Icon name="trash" size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-back" onClick={close}>
      <div className="modal history" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Version history</h2>
          <button className="icon-btn" onClick={close}><Icon name="close" /></button>
        </div>
        <div className="modal-body">
          {error ? (
            <div className="muted">{error}</div>
          ) : (
            <>
              <div className="history-save">
                <input className="text" placeholder="Name this version, e.g. “before the boss room”" value={label} onChange={(e) => setLabel(e.target.value)} />
                <button
                  className="btn primary"
                  disabled={busy || !list}
                  onClick={() =>
                    void run(async () => {
                      const v = await saveVersion(label.trim() || `Version ${pinned.length + 1}`);
                      if (!v) throw new Error('Could not save the version (server unavailable)');
                      setLabel('');
                    }, 'Version saved')
                  }
                >
                  <Icon name="star" size={14} /> Save version
                </button>
              </div>
              <p className="muted small">
                Changes save automatically. A snapshot is taken when you start editing and every 10 minutes while you edit. Old snapshots are thinned out to at most 30:
                up to 6 from the last hour, then one per hour, one per day and one per week further back. Named versions are kept until you delete them.
              </p>
              {list === null && <div className="muted">Loading…</div>}
              {pinned.length > 0 && (
                <>
                  <h3>Named versions ({pinned.length})</h3>
                  <div className="version-list">{pinned.map(card)}</div>
                </>
              )}
              {list && (
                <>
                  <h3>Automatic snapshots ({auto.length})</h3>
                  {!auto.length && <div className="muted small">None yet. The first one is taken when you start editing this level.</div>}
                  <div className="version-list">{auto.map(card)}</div>
                  <p className="muted small">{list.length} versions · {totalKb} KB stored</p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
